import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiExternalLink, FiX } from 'react-icons/fi';
import Pagination from '../components/Pagination';
import { apiRequest } from '../utils/auth';
import { formatCount, formatDateTime, formatMoney } from './format';
import FinancialTransactionDetails from './FinancialTransactionDetails';
import { payerPrimaryName } from './payerIdentity';
import type { FinancialPayer, FinancialPayerHistory } from './types';
import './PayerHistoryModal.css';

type RequestState = { key: string; data: FinancialPayerHistory | null; error: string | null; loading: boolean };

export default function PayerHistoryModal({ payer, environment, onClose }: {
  payer: FinancialPayer;
  environment: 'production' | 'sandbox';
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [request, setRequest] = useState<RequestState>({ key: '', data: null, error: null, loading: true });
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const key = `${payer.userId}:${environment}:${page}:${refresh}`;
  const current = request.key === key ? request : { data: null, error: null, loading: true };

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ environment, page: String(page) });
    setRequest({ key, data: null, error: null, loading: true });
    apiRequest<FinancialPayerHistory>(`/api/admin/revenue/payers/${encodeURIComponent(payer.userId)}/payments?${params}`, { signal: controller.signal })
      .then((data) => {
        if ((data as { success?: boolean }).success === false) throw new Error('The payment service did not return a successful response.');
        if (!controller.signal.aborted) setRequest({ key, data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setRequest({ key, data: null, error: error instanceof Error ? error.message : 'Unable to load payment history.', loading: false });
      });
    return () => controller.abort();
  }, [environment, key, page, payer.userId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])') || []);
      if (!focusable.length) return;
      if (!dialog.current?.contains(document.activeElement)) { event.preventDefault(); closeButton.current?.focus(); return; }
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; };
  }, [onClose]);

  return <div className="financial-history-layer" role="presentation" onMouseDown={onClose}>
    <section ref={dialog} className="financial-history-modal" role="dialog" aria-modal="true" aria-labelledby="payer-history-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="financial-history-header">
        <div>
          <span className="financial-history-eyebrow">Payment history</span>
          <h2 id="payer-history-title">{payerPrimaryName(payer)}</h2>
          <p>{payer.userId} · All dates · {environment === 'sandbox' ? 'Sandbox' : 'Production'} · All currencies</p>
        </div>
        <div className="financial-history-actions">
          <Link to={`/users/${encodeURIComponent(payer.userId)}`}>User profile <FiExternalLink /></Link>
          <button ref={closeButton} type="button" onClick={onClose} aria-label="Close payment history"><FiX /></button>
        </div>
      </header>
      <div className="financial-history-body">
        {current.loading ? <div className="financial-loading" aria-live="polite">Loading payment history…</div> : current.error ? <div className="financial-error" role="alert"><strong>Payment history unavailable</strong><span>{current.error}</span><button type="button" onClick={() => setRefresh((value) => value + 1)}>Retry</button></div> : current.data ? <>
          <p className="financial-history-count">{formatCount(current.data.total)} recorded paid {current.data.total === 1 ? 'transaction' : 'transactions'}</p>
          {current.data.rows.length ? current.data.rows.map((row) => <details className="financial-history-record" key={row.id}>
            <summary>
              <span><strong>{formatDateTime(row.occurredAt || row.createdAt)} · HKT</strong>{!row.occurredAt && row.createdAt ? <small>Creation time fallback</small> : null}<small>{row.store} · {row.eventKind} · {row.status}</small></span>
              <span>{row.productId}</span>
              <span><strong>{formatMoney(row.grossAmount, row.currency)}</strong><small>Cumulative refund {formatMoney(row.refundedAmount, row.currency)}</small></span>
            </summary>
            <div className="financial-history-detail"><FinancialTransactionDetails row={row} embedded /></div>
          </details>) : <div className="financial-empty">{current.data.total ? 'No payments on this page.' : `No paid provider transactions recorded for this user in ${environment}.`}</div>}
          <Pagination variant="analytics" currentPage={current.data.page} totalPages={current.data.pages} onPageChange={setPage} />
          <details className="analytics-method financial-history-method"><summary>What this history includes</summary><p>{current.data.historyScope}</p></details>
        </> : null}
      </div>
    </section>
  </div>;
}
